import { Delete } from '@mui/icons-material';
import { Box, CircularProgress, Link, Stack, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { RequestAttachmentInterface } from '../../interfaces';
import { useRequestAttachmentService, useTranslation } from '../../services';
import { AddAttachmentDialog } from '../AddAttachmentDialog';
import { ButtonWithNotes } from '../ButtonWithNotes';
const apiUrl = process.env.REACT_APP_API_URL || '';

const InnerTableAttachments: FC<{ request: string }> = ({ request }) => {
	const { t } = useTranslation();
	const {
		attachments,
		getRequestAttachmentsByRequestId,
		addAttachments,
		removeAttachment,
	} = useRequestAttachmentService();
	const [relatedAttachments, setRelatedAttachments] = useState<
		RequestAttachmentInterface[]
	>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [countTry, setCountTry] = useState<number>(0);

	useEffect(() => {
		const reqAttachments = attachments.filter(
			(attachment) => attachment.request === request
		);
		if (countTry < 3 && reqAttachments.length === 0) {
			(async () => {
				setLoading(true);
				await getRequestAttachmentsByRequestId(request);
				setLoading(false);
				setCountTry(countTry + 1);
			})();
		} else {
			setRelatedAttachments(reqAttachments);
		}
	}, [attachments, countTry]);

	const handleDeleteAttachment = useCallback(
		async (attachmentId: string, value: boolean, notes: string) => {
			if (value) {
				await removeAttachment(attachmentId, request, notes);
			}
		},
		[removeAttachment]
	);

	const handleAddAttachments = useCallback(
		async (val: { notes: string; requestAttachments: any[] }) => {
			const { notes, requestAttachments } = val;
			setLoading(true);
			await addAttachments(requestAttachments, request, notes);
			setLoading(false);
		},
		[addAttachments]
	);

	return (
		<Box sx={{ m: 1 }}>
			<Stack direction="row" spacing={2} alignItems="center">
				<Typography variant="h6" gutterBottom component="div">
					{t('Request Attachments')}
				</Typography>
				<AddAttachmentDialog onSubmit={handleAddAttachments} />
			</Stack>

			{loading ? (
				<Stack direction="row" justifyContent="center" alignItems="center">
					<CircularProgress color="inherit" sx={{ m: 1 }} />
					{t('Please wait...')}
				</Stack>
			) : (
				<Stack>
					{relatedAttachments.length === 0 && countTry === 3 && (
						<Box alignSelf="center">{t('Not Found')}</Box>
					)}
					{relatedAttachments?.map((attachment, i) => (
						<Stack
							direction="row"
							spacing={2}
							key={attachment.id}
							alignItems="center"
							justifyContent="end"
						>
							<Link href={attachment.link} target="_blank" rel="noreferrer">
								{t('attachment')}
								{i + 1}
							</Link>
							{attachment.id && (
								<ButtonWithNotes
									WrappedComponent={Delete}
									callback={(value, notes) =>
										handleDeleteAttachment(
											attachment.id || '',
											value,
											notes
										)
									}
									propsWrapped={{
										title: t('Delete attachment'),
										color: 'error',
									}}
								/>
							)}
						</Stack>
					))}
				</Stack>
			)}
		</Box>
	);
};

export default InnerTableAttachments;
